import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FieldPreview } from "./FieldPreview";
import { EditorField, useEditorStore } from "../../store";

interface SortableFieldProps {
  field: EditorField;
  isSelected: boolean;
  editorMode: "edit" | "preview";
  onClick: (e: React.MouseEvent) => void;
  error?: string;
  style?: React.CSSProperties;
}

const DragHandleIcon = () => (
  <svg
    viewBox="0 0 20 20"
    className="w-4 h-4 text-gray-400"
    fill="currentColor"
  >
    <path d="M7 2a2 2 0 1 0-.001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0-.001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0-.001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0-.001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0-.001 4.001A2 2 0 0 0 13 14z" />
  </svg>
);

const TrashIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 6h18"></path>
    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
  </svg>
);

const LockIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
  </svg>
);

export const SortableField: React.FC<SortableFieldProps> = (props) => {
  const { field, isSelected, editorMode, onClick, error } = props;

  const removeField = useEditorStore((state) => state.removeField);
  const isLocked = field.locked === true || field.required === true;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: field.tempId,
    disabled: editorMode === "preview",
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    position: "relative" as const,
    width: field.style?.width || "100%",
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    removeField(field.tempId);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={editorMode === "edit" ? onClick : undefined}
      {...(editorMode === "edit" ? attributes : {})}
      {...(editorMode === "edit" ? listeners : {})}
      className={`transition-all rounded-lg p-0.5 group outline-none cursor-grab active:cursor-grabbing ${
        isDragging
          ? "opacity-0 pointer-events-none"
          : isSelected && editorMode === "edit"
            ? "ring-2 ring-indigo-500/80 bg-indigo-50/30 shadow-sm z-40"
            : editorMode === "edit"
              ? "hover:bg-gray-50/60 hover:ring-1 hover:ring-gray-300 z-10"
              : "z-10 cursor-default"
      }`}
    >
      {/* Barra de herramientas flotante*/}
      {editorMode === "edit" && (
        <div
          className={`absolute -top-3.5 right-2 flex items-center gap-1 bg-white border border-gray-200 shadow-sm rounded-md px-1 py-0.5 z-50 transition-all duration-200 ${
            isSelected
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-1 pointer-events-none group-hover:pointer-events-auto group-hover:opacity-100 group-hover:translate-y-0"
          }`}
        >
          {/* Indicador visual de arrastre */}
          <div
            className="p-1 rounded flex items-center justify-center text-gray-400"
            title="Arrastra desde cualquier parte del campo"
          >
            <DragHandleIcon />
          </div>

          {/* Divisor vertical */}
          <div className="w-px h-3 bg-gray-200 mx-0.5"></div>

          {/* Botón de Eliminar / Icono de Candado */}
          {!isLocked ? (
            <button
              onPointerDown={(e) => e.stopPropagation()}
              onClick={handleDelete}
              className="p-1 rounded text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors flex items-center justify-center cursor-pointer"
              title="Delete field"
            >
              <TrashIcon />
            </button>
          ) : (
            <div
              className="p-1 rounded text-gray-400 flex items-center justify-center"
              title={
                field.locked
                  ? "Locked fields cannot be deleted"
                  : "Required fields cannot be deleted"
              }
            >
              <LockIcon />
            </div>
          )}
        </div>
      )}

      {/* Contenedor del Input */}
      <div
        className={`w-full relative z-0 ${
          editorMode === "edit" ? "pointer-events-none" : ""
        }`}
      >
        <div className={editorMode === "edit" ? "pointer-events-auto" : ""}>
          <FieldPreview
            key={`${field.tempId}-${editorMode}`}
            field={field}
            error={error}
            style={props.style}
          />
        </div>
      </div>
    </div>
  );
};
